import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Modal,
  Row,
  Col,
  Spinner,
  Badge,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { GrEdit } from "react-icons/gr";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaPlus, FaHistory, FaSearch, FaFileImport, FaDownload, FaHardHat, FaExclamationTriangle, FaBoxes, FaTags } from "react-icons/fa";
import { BiBox, BiPlus, BiHistory, BiEdit, BiTrash, BiSearch, BiDownload, BiFile } from "react-icons/bi";
import * as XLSX from "xlsx";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

// Categorías válidas, incluyendo ropa de trabajo
const CATEGORIAS_VALIDAS = [
  "cabeza",
  "ojos",
  "respiratoria",
  "manos",
  "pies",
  "ropa_alta_visibilidad",
  "ropa_ignifuga",
  "ropa_impermeable",
  "ropa_frio",
  "overol",
  "camisa",
  "pantalon",
  "chaleco",
  "bata",
  "uniforme"
];

function CatalogoEPPs({ usuario }) {
  const ruc = usuario.ruc;
  const [epps, setEpps] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [modoEdicion, setModoEdicion] = useState(false);
  const [eppEditado, setEppEditado] = useState(null);

  // Para añadir stock y kardex
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockData, setStockData] = useState({ id: null, cantidad: 0, guia: "" });

  // Para mostrar historial kardex
  const [showKardexModal, setShowKardexModal] = useState(false);
  const [kardexMovimientos, setKardexMovimientos] = useState([]);
  const [kardexEpp, setKardexEpp] = useState(null);

  // Estado para el formulario
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    categoria: "",
    stock: 0,
    marca: "",
    modelo: "",
    talla: "",
    color: "",
    codigo: "",
    periodoReposicion: 6, // meses por defecto
    unidadReposicion: "meses",
  });
  
  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    bajoStock: 0,
    categorias: 0,
    totalStock: 0
  });

  useEffect(() => {
    if (ruc) cargarEpps();
  }, [ruc]);

  const cargarEpps = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "epp_catalogo"), where("ruc_empresa", "==", ruc));
      const querySnapshot = await getDocs(q);
      const eppsData = [];
      let bajoStockCount = 0;
      let totalStockSum = 0;
      const categoriasSet = new Set();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        eppsData.push({ id: doc.id, ...data });
        
        if (parseInt(data.stock || 0) <= 5) bajoStockCount++;
        totalStockSum += parseInt(data.stock || 0);
        if (data.categoria) categoriasSet.add(data.categoria);
      });

      setEpps(eppsData);
      setStats({
        total: eppsData.length,
        bajoStock: bajoStockCount,
        categorias: categoriasSet.size,
        totalStock: totalStockSum
      });
    } catch (error) {
      console.error("Error al cargar EPPs:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los EPPs",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validación de categoría
      if (!CATEGORIAS_VALIDAS.includes(formData.categoria)) {
        Swal.fire({
          title: "Error",
          text: "La categoría seleccionada no es válida.",
          icon: "error",
        });
        setLoading(false);
        return;
      }

      // Validación de código único
      const codigoExistente = epps.some(
        (epp) =>
          String(epp.codigo) === String(formData.codigo) &&
          (!modoEdicion || epp.id !== eppEditado?.id)
      );
      if (codigoExistente) {
        Swal.fire({
          title: "Error",
          text: "El código ya existe en el catálogo.",
          icon: "error",
        });
        setLoading(false);
        return;
      }

      if (modoEdicion) {
        const docRef = doc(db, "epp_catalogo", eppEditado.id);
        await updateDoc(docRef, {
          ...formData,
          stock: parseInt(formData.stock),
          codigo: parseInt(formData.codigo),
          periodoReposicion: parseInt(formData.periodoReposicion),
          unidadReposicion: formData.unidadReposicion,
          fechaActualizacion: new Date(),
          ruc_empresa: ruc,
        });

        // AUDITORÍA EDICIÓN EPP
        try {
          await logBusinessAction("EDICION_EPP", "GestionEPP", {
            id: eppEditado.id,
            codigo: formData.codigo,
            nombre: formData.nombre,
            categoria: formData.categoria,
            cambios: {
              nombre: { anterior: eppEditado.nombre, nuevo: formData.nombre },
              stock: { anterior: eppEditado.stock, nuevo: formData.stock }
            }
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de edición de EPP", e);
        }
      } else {
        const docAddedRef = await addDoc(collection(db, "epp_catalogo"), {
          ...formData,
          stock: parseInt(formData.stock),
          codigo: parseInt(formData.codigo),
          periodoReposicion: parseInt(formData.periodoReposicion),
          unidadReposicion: formData.unidadReposicion,
          fechaRegistro: new Date(),
          activo: true,
          ruc_empresa: ruc,
        });

        // AUDITORÍA CREACIÓN EPP
        try {
          await logBusinessAction("CREACION_EPP", "GestionEPP", {
            id: docAddedRef.id,
            codigo: formData.codigo,
            nombre: formData.nombre,
            categoria: formData.categoria,
            stock: formData.stock
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de creación de EPP", e);
        }
      }

      await cargarEpps();
      limpiarFormulario();
      setShowModal(false);
      Swal.fire({
        title: "¡Éxito!",
        text: modoEdicion
          ? "EPP actualizado correctamente"
          : "EPP agregado correctamente",
        icon: "success",
      });
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (epp) => {
    setModoEdicion(true);
    setEppEditado(epp);
    setFormData({
      nombre: epp.nombre,
      descripcion: epp.descripcion,
      categoria: epp.categoria,
      stock: epp.stock,
      marca: epp.marca,
      modelo: epp.modelo,
      talla: epp.talla || "",
      color: epp.color || "",
      codigo: epp.codigo,
      periodoReposicion: epp.periodoReposicion || 6,
      unidadReposicion: epp.unidadReposicion || "meses",
    });
    setShowModal(true);
  };

  const handleEliminar = async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const eppToDelete = epps.find(e => e.id === id);
        await deleteDoc(doc(db, "epp_catalogo", id));
        await cargarEpps();
        Swal.fire("¡Eliminado!", "El EPP ha sido eliminado.", "success");

        // AUDITORÍA ELIMINACIÓN EPP
        try {
          await logBusinessAction("ELIMINACION_EPP", "GestionEPP", {
            id,
            codigo: eppToDelete?.codigo,
            nombre: eppToDelete?.nombre
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de eliminación de EPP", e);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar el EPP",
        icon: "error",
      });
    }
  };

  const limpiarFormulario = () => {
    setFormData({
      nombre: "",
      descripcion: "",
      categoria: "",
      stock: 0,
      marca: "",
      modelo: "",
      talla: "",
      color: "",
      codigo: "",
      periodoReposicion: 6,
      unidadReposicion: "meses",
    });
    setModoEdicion(false);
    setEppEditado(null);
  };

  // Descargar plantilla Excel
  const handleDescargarPlantilla = () => {
    // 1. Hoja de Plantilla con Ejemplo
    const data = [
      [
        "codigo", "nombre", "categoria", "stock", "talla", "descripcion", 
        "marca", "modelo", "color", "periodoReposicion", "unidadReposicion"
      ],
      [
        "100001", "CASCO DE SEGURIDAD BLANCO", "cabeza", "10", "ESTÁNDAR", "Casco con suspensión de 4 puntos", 
        "3M", "H-700", "Blanco", "12", "meses"
      ],
      [
        "100002", "GUANTES DE NITRILO", "manos", "50", "L", "Guantes resistentes a químicos", 
        "ANSELL", "SOLVEX", "Verde", "1", "meses"
      ]
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PlantillaEPPs");

    // 2. Hoja de Ayuda (Instrucciones)
    const helpData = [
      ["COLUMNA", "DESCRIPCIÓN", "OBLIGATORIO", "VALORES RECOMENDADOS / EJEMPLO"],
      ["codigo", "Código único del producto (Numérico o Texto)", "SI", "100001"],
      ["nombre", "Nombre descriptivo del EPP", "SI", "LENTES DE SEGURIDAD"],
      ["categoria", "Categoría del sistema (Ver lista abajo)", "SI", "ojos, cabeza, manos, pies, etc."],
      ["stock", "Cantidad inicial en almacén", "SI", "15"],
      ["talla", "Talla del EPP", "NO", "S, M, L, XL, ESTÁNDAR"],
      ["periodoReposicion", "Número para la alerta de renovación", "NO", "6 (indica 6 meses)"],
      ["unidadReposicion", "Unidad del periodo", "NO", "meses, dias"],
      ["", "", "", ""],
      ["CATEGORÍAS VÁLIDAS (Escribir exactamente así):", "", "", ""],
      [CATEGORIAS_VALIDAS.join(", "), "", "", ""],
      ["", "", "", ""],
      ["NOTA IMPORTANTE:", "No cambie el nombre de los encabezados de la primera fila.", "", ""]
    ];
    const wsHelp = XLSX.utils.aoa_to_sheet(helpData);
    XLSX.utils.book_append_sheet(wb, wsHelp, "AYUDA_INSTRUCCIONES");

    XLSX.writeFile(wb, "plantilla_epps_con_guia.xlsx");
  };

  // Importar EPPs desde Excel
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      // Obtener todos los códigos existentes
      const querySnapshot = await getDocs(collection(db, "epp_catalogo"));
      const codigosExistentes = new Set();
      querySnapshot.forEach((doc) => {
        codigosExistentes.add(String(doc.data().codigo));
      });

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      // Validar y mapear los datos
      const nuevosEpps = rows.map((row) => ({
        codigo: parseInt(row.codigo || row.Código || ""),
        nombre: row.nombre || row.Nombre || "",
        categoria: (row.categoria || row.Categoría || "").toLowerCase(),
        stock: parseInt(row.stock || row.Stock || 0),
        talla: row.talla || row.Talla || "",
        descripcion: row.descripcion || row.Descripción || "",
        marca: row.marca || row.Marca || "",
        modelo: row.modelo || row.Modelo || "",
        color: row.color || row.Color || "",
        periodoReposicion: parseInt(row.periodoReposicion || row["periodo de reposicion"] || row["periodo_reposicion"] || 6),
        unidadReposicion: row.unidadReposicion || row["unidad de reposicion"] || row["unidadReposicion"] || "meses",
        fechaRegistro: new Date(),
        activo: true,
        ruc_empresa: ruc,
      }));

      // Filtrar SOLO los que tengan todos los campos obligatorios, categoría válida y código único
      const validos = nuevosEpps.filter(
        epp =>
          epp.codigo &&
          !codigosExistentes.has(String(epp.codigo)) &&
          epp.nombre &&
          epp.categoria &&
          CATEGORIAS_VALIDAS.includes(epp.categoria) &&
          epp.stock &&
          epp.talla
      );

      // Detectar filas inválidas o duplicadas
      const invalidos = nuevosEpps.length - validos.length;

      // Guardar en Firestore solo los válidos y actualizar el set de códigos
      for (const epp of validos) {
        await addDoc(collection(db, "epp_catalogo"), epp);
        codigosExistentes.add(String(epp.codigo)); // Evitar duplicados en el mismo archivo
      }

      await cargarEpps();
      Swal.fire(
        "¡Éxito!",
        `Se importaron ${validos.length} EPPs correctamente.${invalidos > 0 ? `\n${invalidos} filas fueron ignoradas por faltar campos obligatorios, categoría inválida o código duplicado.` : ""}`,
        "success"
      );
    } catch (error) {
      console.error("Error al importar:", error);
      Swal.fire("Error", "No se pudo importar el archivo. Revise el formato.", "error");
    } finally {
      setLoading(false);
      e.target.value = ""; // Limpiar input
    }
  };

  // Añadir stock y registrar en kardex
  const handleAbrirStock = (epp) => {
    setStockData({ id: epp.id, cantidad: 0, guia: "" });
    setShowStockModal(true);
  };

  const handleAgregarStock = async () => {
    if (!stockData.cantidad || !stockData.guia) {
      Swal.fire("Error", "Debe ingresar la cantidad y la guía de remisión.", "error");
      return;
    }
    setLoading(true);
    try {
      // Actualizar stock en catálogo
      const eppRef = doc(db, "epp_catalogo", stockData.id);
      const eppActual = epps.find(e => e.id === stockData.id);
      const nuevoStock = parseInt(eppActual.stock) + parseInt(stockData.cantidad);
      await updateDoc(eppRef, {
        stock: nuevoStock,
        fechaUltimaReposicion: new Date()
      });

      // Registrar movimiento en kardex
      await addDoc(collection(db, "epp_kardex"), {
        eppId: stockData.id,
        codigo: eppActual.codigo,
        nombre: eppActual.nombre,
        tipo: "ingreso",
        cantidad: parseInt(stockData.cantidad),
        guiaRemision: stockData.guia,
        fecha: new Date()
      });

      // AUDITORÍA INGRESO STOCK EPP
      try {
        await logBusinessAction("ACTUALIZACION_INVENTARIO_EPP", "GestionEPP", {
          id: stockData.id,
          codigo: eppActual.codigo,
          nombre: eppActual.nombre,
          cantidadAgregada: parseInt(stockData.cantidad),
          guia: stockData.guia,
          nuevoStock
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de ingreso de stock", e);
      }

      await cargarEpps();
      setShowStockModal(false);
      Swal.fire("¡Éxito!", "Stock actualizado y movimiento registrado.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo actualizar el stock.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Mostrar historial kardex
  const handleVerKardex = async (epp) => {
    setKardexEpp(epp);
    setShowKardexModal(true);
    setLoading(true);
    try {
      const q = query(
        collection(db, "epp_kardex"),
        where("eppId", "==", epp.id),
        orderBy("fecha", "desc")
      );
      const snapshot = await getDocs(q);
      const movimientos = [];
      snapshot.forEach(doc => movimientos.push({ id: doc.id, ...doc.data() }));
      console.log("Movimientos encontrados:", movimientos); // <-- Agrega esto
      setKardexMovimientos(movimientos);
    } catch (error) {
      console.error("Error consultando kardex:", error); // <-- Agrega esto
      setKardexMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  const eppsFiltrados = epps.filter((epp) => {
    const cumpleBusqueda =
      epp.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      String(epp.codigo).includes(busqueda);
    const cumpleCategoria =
      !filtroCategoria || epp.categoria === filtroCategoria;
    return cumpleBusqueda && cumpleCategoria;
  });

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        padding: "20px 0",
      }}
    >
      <Container fluid className="px-lg-5 px-3 pb-5">
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden", minHeight: "85vh" }}
        >
          {/* Header Premium con degradado naranja */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              borderBottom: "3px solid #ea580c",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaHardHat className="me-3" />
                Catálogo de EPPs
              </h2>
              <p className="mb-0 opacity-75">
                Gestión de Inventario de Equipos de Protección Personal
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones (Estilo CentroCostos) */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                        color: "white",
                      }}
                    >
                      <FaHardHat size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Acciones del Catálogo</h5>
                    <p className="text-muted mb-0 small">
                      Agregue nuevos elementos o importe desde archivos Excel
                    </p>
                  </Col>
                  <Col md={4} className="text-end d-flex gap-2 justify-content-end">
                    <Button
                      variant="light"
                      onClick={() => {
                        limpiarFormulario();
                        setShowModal(true);
                      }}
                      className="d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                    >
                      <FaPlus size="16px" /> Nuevo EPP
                    </Button>
                    <Form.Label
                      className="btn btn-light mb-0 d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", cursor: "pointer" }}
                    >
                      <FaFileImport size="16px" /> Importar
                      <Form.Control
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleImportExcel}
                        style={{ display: "none" }}
                      />
                    </Form.Label>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Fila de KPIs (Estilo CentroCostos) */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.total}</h4>
                    <p className="mb-0 opacity-90 small">Total de Items</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.bajoStock}</h4>
                    <p className="mb-0 opacity-90 small">Bajo Stock (≤ 5)</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.totalStock}</h4>
                    <p className="mb-0 opacity-90 small">Stock Total Físico</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.categorias}</h4>
                    <p className="mb-0 opacity-90 small">Categorías Activas</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Barra de Búsqueda y Filtros Premium */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-3">
                  <Col md={5}>
                    <div className="position-relative">
                      <FaSearch
                        className="position-absolute"
                        style={{ left: "15px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre o código..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="ps-5 form-control-lg"
                        style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={filtroCategoria}
                      onChange={(e) => setFiltroCategoria(e.target.value)}
                      className="form-control-lg"
                      style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                    >
                      <option value="">Todas las categorías</option>
                      {CATEGORIAS_VALIDAS.map(cat => (
                        <option key={cat} value={cat}>{cat.replace(/_/g, " ").toUpperCase()}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3} className="d-flex justify-content-end align-items-center">
                    <Button
                      variant="outline-warning"
                      onClick={handleDescargarPlantilla}
                      className="d-flex align-items-center gap-2 py-2 px-3 fw-bold w-100"
                      style={{ borderRadius: "10px", border: "2px solid #f97316" }}
                    >
                      <FaDownload /> Plantilla Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Tabla de Resultados (Estilo Premium) */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                  border: "none"
                }}
              >
                <div className="d-flex align-items-center">
                  <FaBoxes className="me-2" />
                  <div className="flex-grow-1">
                    <h5 className="mb-0 fw-semibold">Inventario General de EPPs</h5>
                    <small className="opacity-75">Visualización detallada y control de stock</small>
                  </div>
                  <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                    {eppsFiltrados.length} Registros
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                  <Table hover className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Código</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Nombre del EPP</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Categoría</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Stock</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Talla</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Reposición</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eppsFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <FaBoxes size="4rem" className="text-muted mb-3 opacity-50" />
                            <h5 className="text-muted">No se encontraron equipos</h5>
                            <p className="text-muted small">Cree un nuevo EPP o ajuste su búsqueda.</p>
                          </td>
                        </tr>
                      ) : (
                        eppsFiltrados.map((epp) => (
                          <tr key={epp.id}>
                            <td className="py-3 px-3 text-center fw-bold text-dark">{epp.codigo}</td>
                            <td className="py-3 px-3">
                              <div className="fw-bold text-dark">{epp.nombre}</div>
                              <small className="text-muted">{epp.marca} - {epp.modelo}</small>
                            </td>
                            <td className="py-3 px-3">
                              <Badge bg="info" className="text-capitalize" style={{ fontWeight: "500" }}>
                                {epp.categoria.replace(/_/g, " ")}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Badge
                                bg={parseInt(epp.stock) <= 5 ? "danger" : parseInt(epp.stock) <= 15 ? "warning" : "success"}
                                pill
                                style={{ fontSize: "0.85rem", minWidth: "45px" }}
                              >
                                {epp.stock}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="text-dark fw-medium">{epp.talla || "N/A"}</span>
                            </td>
                            <td className="py-3 px-3 text-center text-muted small">
                              {epp.periodoReposicion
                                ? `Cada ${epp.periodoReposicion} ${epp.unidadReposicion}`
                                : "N/D"}
                            </td>
                            <td className="py-3 px-3">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleAbrirStock(epp)}
                                  style={{ borderRadius: "6px", padding: "4px 8px" }}
                                  title="Añadir stock"
                                >
                                  <FaPlus size="14px" />
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleVerKardex(epp)}
                                  style={{ borderRadius: "6px", padding: "4px 8px" }}
                                  title="Historial Kardex"
                                >
                                  <FaHistory size="14px" />
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditar(epp)}
                                  style={{ borderRadius: "6px", padding: "4px 8px" }}
                                  title="Editar"
                                >
                                  <BiEdit size="14px" />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleEliminar(epp.id)}
                                  style={{ borderRadius: "6px", padding: "4px 8px" }}
                                  title="Eliminar"
                                >
                                  <BiTrash size="14px" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal Agregar/Editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header
          closeButton
          className="text-white border-0 pb-0"
        >
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}
            >
              <FaHardHat size={20} />
            </div>
            {modoEdicion ? "Editar EPP" : "Nuevo Equipo de Protección"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Código</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.codigo}
                    onChange={(e) =>
                      setFormData({ ...formData, codigo: e.target.value })
                    }
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Nombre</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre}
                    onChange={(e) =>
                      setFormData({ ...formData, nombre: e.target.value })
                    }
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.descripcion}
                    onChange={(e) =>
                      setFormData({ ...formData, descripcion: e.target.value })
                    }
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Categoría</Form.Label>
                  <Form.Select
                    value={formData.categoria}
                    onChange={(e) =>
                      setFormData({ ...formData, categoria: e.target.value })
                    }
                    required
                    className="form-control-lg"
                  >
                    <option value="">Seleccione una categoría</option>
                    <option value="cabeza">Protección para la cabeza</option>
                    <option value="ojos">Protección para los ojos</option>
                    <option value="respiratoria">Protección respiratoria</option>
                    <option value="manos">Protección para las manos</option>
                    <option value="pies">Protección para los pies</option>
                    <option value="ropa_alta_visibilidad">Ropa de alta visibilidad</option>
                    <option value="ropa_ignifuga">Ropa ignífuga</option>
                    <option value="ropa_impermeable">Ropa impermeable</option>
                    <option value="ropa_frio">Ropa para el frío</option>
                    <option value="overol">Overol</option>
                    <option value="camisa">Camisa</option>
                    <option value="pantalon">Pantalón</option>
                    <option value="chaleco">Chaleco</option>
                    <option value="bata">Bata</option>
                    <option value="uniforme">Uniforme</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Stock</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.stock}
                    onChange={(e) =>
                      setFormData({ ...formData, stock: e.target.value })
                    }
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Marca</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.marca}
                    onChange={(e) =>
                      setFormData({ ...formData, marca: e.target.value })
                    }
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.modelo}
                    onChange={(e) =>
                      setFormData({ ...formData, modelo: e.target.value })
                    }
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Talla</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.talla}
                    onChange={(e) =>
                      setFormData({ ...formData, talla: e.target.value })
                    }
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Color</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Periodo de reposición</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={formData.periodoReposicion}
                    onChange={(e) =>
                      setFormData({ ...formData, periodoReposicion: e.target.value })
                    }
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Unidad de reposición</Form.Label>
                  <Form.Select
                    value={formData.unidadReposicion}
                    onChange={(e) =>
                      setFormData({ ...formData, unidadReposicion: e.target.value })
                    }
                    className="form-control-lg"
                  >
                    <option value="días">Días</option>
                    <option value="meses">Meses</option>
                    <option value="años">Años</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2 justify-content-end mt-4">
              <Button
                variant="light"
                size="lg"
                onClick={() => setShowModal(false)}
                className="px-4"
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
                size="lg"
                className="px-4"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Guardando...
                  </>
                ) : (
                  modoEdicion ? "Actualizar" : "Guardar"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal Añadir Stock */}
      <Modal show={showStockModal} onHide={() => setShowStockModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}
            >
              <FaPlus size={20} />
            </div>
            Actualizar Inventario
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Cantidad a añadir <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={stockData.cantidad}
                onChange={e => setStockData({ ...stockData, cantidad: e.target.value })}
                className="form-control-lg"
                placeholder="0"
                style={{ borderRadius: "10px" }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Guía de remisión / Referencia <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={stockData.guia}
                onChange={e => setStockData({ ...stockData, guia: e.target.value })}
                className="form-control-lg"
                placeholder="Ej: GR-001-2024"
                style={{ borderRadius: "10px" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={() => setShowStockModal(false)} style={{ borderRadius: "8px" }}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleAgregarStock}
            disabled={loading}
            style={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              border: "none",
              padding: "8px 25px",
              fontWeight: "600"
            }}
          >
            {loading ? "Guardando..." : "Confirmar Ingreso"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Kardex */}
      <Modal show={showKardexModal} onHide={() => setShowKardexModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(13, 202, 240, 0.1)", color: "#0dcaf0" }}
            >
              <FaHistory size={20} />
            </div>
            Historial de Movimientos: {kardexEpp?.nombre}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="info" />
              <p className="mt-2 text-muted">Consultando movimientos...</p>
            </div>
          ) : kardexMovimientos.length === 0 ? (
            <div className="text-center py-5">
              <BiHistory size="4rem" className="text-muted mb-3 opacity-25" />
              <p className="text-muted">No hay movimientos registrados para este equipo.</p>
            </div>
          ) : (
            <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <div className="table-responsive" style={{ maxHeight: "400px" }}>
                <Table hover className="align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="py-3 px-3 border-0">Fecha y Hora</th>
                      <th className="py-3 px-3 border-0 text-center">Tipo</th>
                      <th className="py-3 px-3 border-0 text-center">Cantidad</th>
                      <th className="py-3 px-3 border-0">Documento / Guía</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kardexMovimientos.map((mov) => (
                      <tr key={mov.id}>
                        <td className="py-2 px-3">
                          {mov.fecha?.toDate ? mov.fecha.toDate().toLocaleString() : new Date(mov.fecha).toLocaleString()}
                        </td>
                        <td className="text-center">
                          <Badge bg={mov.tipo === "ingreso" ? "success" : "danger"} pill>
                            {mov.tipo?.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="text-center fw-bold">
                          {mov.tipo === "ingreso" ? `+${mov.cantidad}` : `-${mov.cantidad}`}
                        </td>
                        <td className="text-muted">{mov.guiaRemision}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowKardexModal(false)} style={{ borderRadius: "8px" }}>
            Cerrar Historial
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CatalogoEPPs;